import type { SnEvent } from './types'
import { formatEventAddress } from './formatEventAddress'

type EventLocationFields = Pick<
  SnEvent,
  'venue' | 'facility' | 'address' | 'address_name'
>

/**
 * One-line location for an event.
 *
 * Events carry the same venue/facility/address triple games do, so the venue
 * form matches `CenterInfo`'s `"{venue} - {facility}"` exactly — a mixed list
 * should not render the same place two different ways.
 *
 * Falls back to the address when there is no venue, since an event can be
 * booked somewhere that has no curated venue record. Returns '' when nothing
 * is set, which callers use to hide the row entirely.
 */
export const formatEventLocation = ({
  venue,
  facility,
  address,
  address_name,
}: EventLocationFields): string => {
  const venueName = venue?.name?.trim()

  if (venueName) {
    const facilityName = facility?.name?.trim()
    return facilityName ? `${venueName} - ${facilityName}` : venueName
  }

  // No venue record. A captured location NAME reads better than a street
  // ("North Vancouver Sports Centre" vs "155 17th Street E"), so it wins;
  // the street is the fallback when no name was entered.
  const addressName = address_name?.trim()
  if (addressName) {
    return addressName
  }

  return formatEventAddress(address ?? null)
}

/**
 * The STREET half of an event's location — never the venue/location name.
 *
 * The detail screens show the name on one row ("North Vancouver Sports Center
 * - Eagles Stadium") and the postal address on the next, so this must resolve
 * to a street or to '' — returning the venue name here printed the same string
 * in both rows and hid the address entirely.
 *
 * A venue-backed event leaves the event's top-level `address` null and carries
 * the street on the venue itself, so `venue.address` is checked first. Falls
 * back to the event's own address, which is where the custom-address path
 * stores it. Returns '' when neither exists, which callers use to hide the
 * address row and show only the name.
 */
export const formatEventStreetAddress = ({
  venue,
  address,
}: EventLocationFields): string =>
  formatEventAddress(venue?.address ?? address ?? null)

/**
 * Location name AND street address on one line, for a list row that has room
 * for both — e.g. "Main Arena - 155 17th Street E, North Vancouver".
 *
 * `formatEventLocation` picks ONE of the two (name preferred) for callers that
 * only have room for a single short string; this shows both, which is what a
 * reader scanning a schedule actually needs — the name to recognise the place
 * and the street to find it.
 *
 * Either half may be missing, in which case the other stands alone. When the
 * two would resolve to the same string (a curated venue whose name matches the
 * captured location name) it is printed once rather than twice.
 *
 * The name half comes from `formatEventLocation`, NOT `address_name` — a
 * venue-backed event has no `address_name`, so reading that directly would
 * print a bare street and drop the venue name the row exists to show.
 */
export const formatEventLocationFull = (
  fields: EventLocationFields
): string => {
  const name = formatEventLocation(fields)
  const street = formatEventStreetAddress(fields)

  if (name && street && name !== street) {
    return `${name} - ${street}`
  }

  return name || street
}
